import {
    FETCH_LANGUAGE_DATA_REQUEST,
    FETCH_LANGUAGE_DATA_SUCCESS,
    FETCH_LANGUAGE_DATA_FAILURE,
    UPLOAD_LANGUAGE_FILE_START,
    UPLOAD_LANGUAGE_FILE_PROGRESS,
    UPLOAD_LANGUAGE_FILE_SUCCESS,
    UPLOAD_LANGUAGE_FILE_FAILURE,
    UPLOAD_LANGUAGE_FILE_CANCEL,
    IMPORT_LANGUAGE_DATA_REQUEST,
    IMPORT_LANGUAGE_DATA_SUCCESS,
    IMPORT_LANGUAGE_DATA_FAILURE,
    UPDATE_LANGUAGE_SELECTION,
    DOWNLOAD_TEMPLATE_REQUEST,
    TOGGLE_DOWNLOAD_SECTION,
    DOWNLOAD_TEMPLATE_SUCCESS,
    DOWNLOAD_TEMPLATE_FAILURE,
    RESET_LANGUAGE_DIALOG_STATE,
    SET_FILE_FORMAT,
    REMOVE_UPLOADED_FILE,
    SHOW_CONFIRMATION_DIALOG,
    HIDE_CONFIRMATION_DIALOG,
    SET_INITIAL_SELECTION,
} from './AddLanguageDialogTypes';
import * as XLSX from 'xlsx';

export const fetchLanguageDataRequest = () => {
    return {
        type: FETCH_LANGUAGE_DATA_REQUEST
    };
};

export const fetchLanguageDataSuccess = (data) => {
    return {
        type: FETCH_LANGUAGE_DATA_SUCCESS,
        payload: data
    };
};

export const fetchLanguageDataFailure = (error) => {
    return {
        type: FETCH_LANGUAGE_DATA_FAILURE,
        payload: error
    };
};

export const uploadLanguageFileStart = (file) => ({
    type: UPLOAD_LANGUAGE_FILE_START,
    payload: file
});

export const uploadLanguageFileProgress = (progress) => ({
    type: UPLOAD_LANGUAGE_FILE_PROGRESS,
    payload: progress
});

export const uploadLanguageFileSuccess = (file, parsedData) => ({
    type: UPLOAD_LANGUAGE_FILE_SUCCESS,
    payload: { file, parsedData }
});

export const uploadLanguageFileFailure = (error) => ({
    type: UPLOAD_LANGUAGE_FILE_FAILURE,
    payload: error
});

export const uploadLanguageFileCancel = () => ({
    type: UPLOAD_LANGUAGE_FILE_CANCEL
});

export const importLanguageDataRequest = () => {
    return {
        type: IMPORT_LANGUAGE_DATA_REQUEST
    };
};

export const importLanguageDataSuccess = () => {
    return {
        type: IMPORT_LANGUAGE_DATA_SUCCESS
    };
};

export const importLanguageDataFailure = (error) => {
    return {
        type: IMPORT_LANGUAGE_DATA_FAILURE,
        payload: error
    };
};

export const updateLanguageSelection = (languages) => {
    return {
        type: UPDATE_LANGUAGE_SELECTION,
        payload: languages
    };
};

export const downloadTemplateRequest = () => {
    return {
        type: DOWNLOAD_TEMPLATE_REQUEST
    };
};

export const toggleDownloadSection = (show) => ({
    type: TOGGLE_DOWNLOAD_SECTION,
    payload: show
});

export const downloadTemplateSuccess = () => {
    return {
        type: DOWNLOAD_TEMPLATE_SUCCESS
    };
};

export const downloadTemplateFailure = (error) => {
    return {
        type: DOWNLOAD_TEMPLATE_FAILURE,
        payload: error
    };
};

export const setFileFormat = (format) => {
    return {
        type: SET_FILE_FORMAT,
        payload: format
    };
};

export const removeUploadedFile = () => {
    return {
        type: REMOVE_UPLOADED_FILE
    };
};

export const resetLanguageDialogState = () => {
    return {
        type: RESET_LANGUAGE_DIALOG_STATE
    };
};

export const setInitialSelection = (languages) => {
    return {
        type: SET_INITIAL_SELECTION,
        payload: languages,
    };
};

export const showConfirmationDialog = (languagesToRemove, pendingSelection, errorMessage = null) => {
    return {
        type: SHOW_CONFIRMATION_DIALOG,
        payload: {
            languagesToRemove,
            pendingSelection,
            errorMessage,
        },
    };
};

export const hideConfirmationDialog = () => {
    return {
        type: HIDE_CONFIRMATION_DIALOG,
    };
};

export const confirmLanguageRemoval = () => {
    return (dispatch, getState) => {
        const { pendingSelection } = getState().addLanguageDialog;

        dispatch({
            type: UPDATE_LANGUAGE_SELECTION,
            payload: pendingSelection,
        });

        dispatch({
            type: SET_INITIAL_SELECTION,
            payload: pendingSelection,
        });

        dispatch(hideConfirmationDialog());
    };
};

export const cancelLanguageRemoval = () => {
    return (dispatch, getState) => {
        const { initialSelectedLanguages } = getState().addLanguageDialog;

        dispatch({
            type: UPDATE_LANGUAGE_SELECTION,
            payload: initialSelectedLanguages,
        });

        dispatch(hideConfirmationDialog());
    };
};

// Thunk Actions
let uploadIntervalId = null;

export const fetchLanguageData = () => {
    return async function (dispatch) {
        dispatch(fetchLanguageDataRequest());
        try {
            dispatch(fetchLanguageDataSuccess([]));
        } catch (error) {
            dispatch(fetchLanguageDataFailure(error.message));
        }
    };
};

const parseUploadedFileContent = (fileExtension, result) => {
    const emptyFileError = "Metadata file is empty, please check and upload the latest file!";

    if (fileExtension === 'json') {
        if (!result || result.trim() === '') {
            return { error: emptyFileError };
        }
        const jsonData = JSON.parse(result);
        if (!jsonData || (Array.isArray(jsonData) && jsonData.length === 0) ||
            (typeof jsonData === 'object' && Object.keys(jsonData).length === 0)) {
            return { error: emptyFileError };
        }
        return { data: jsonData };
    }

    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        const data = new Uint8Array(result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        if (!jsonData || jsonData.length === 0) {
            return { error: emptyFileError };
        }
        return { data: jsonData };
    }

    return { error: emptyFileError };
};

const findInvalidLanguageTags = (parsedData, validLanguageTags) => {
    const invalidTags = new Set();
    parsedData.forEach(item => {
        [
            item.languages?.languageTag,
            item.descriptions?.language,
            item.legalDocuments?.language,
            item.languageTag,
            item.language
        ].filter(Boolean).forEach(language => {
            if (!validLanguageTags.includes(language)) {
                invalidTags.add(language);
            }
        });
    });
    return invalidTags;
};

const runUploadProgress = (dispatch) => {
    return new Promise((resolve) => {
        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            dispatch(uploadLanguageFileProgress(progress));
            if (progress >= 100) {
                clearInterval(interval);
                uploadIntervalId = null;
                resolve();
            }
        }, 200);
        uploadIntervalId = interval;
    });
};

const getLocalisedName = (languageData, tag) => {
    const langData = languageData.find(l => l.languageTag === tag);
    return langData?.localisedName || tag;
};

const getLanguagesInFile = (parsedData) => {
    const languagesInFile = new Set();
    parsedData.forEach(item => {
        const language = item.languages?.languageTag ||
            item.descriptions?.language ||
            item.legalDocuments?.language ||
            item.languageTag ||
            item.language;
        if (language) {
            languagesInFile.add(language);
        }
    });
    return languagesInFile;
};

export const uploadLanguageFile = (file, defaultLanguage, selectedLanguage, languageData = []) => async (dispatch) => {
    if (uploadIntervalId) {
        clearInterval(uploadIntervalId);
        uploadIntervalId = null;
    }

    try {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const result = fileExtension === 'json' ? await file.text() : await file.arrayBuffer();

        const { data: parsedData, error: parseError } = parseUploadedFileContent(fileExtension, result);

        if (parseError) {
            dispatch({
                type: SHOW_CONFIRMATION_DIALOG,
                payload: {
                    languagesToRemove: [],
                    pendingSelection: [],
                    errorMessage: parseError
                }
            });
            dispatch(uploadLanguageFileFailure("Empty file"));
            return;
        }

        const validLanguageTags = languageData.map(lang => lang.languageTag);
        const invalidTags = findInvalidLanguageTags(parsedData, validLanguageTags);

        if (invalidTags.size > 0) {
            const invalidTagsList = Array.from(invalidTags).join(', ');
            dispatch({
                type: SHOW_CONFIRMATION_DIALOG,
                payload: {
                    languagesToRemove: [],
                    pendingSelection: [],
                    errorMessage: `Meta Data file contains invalid language tag - ${invalidTagsList}, please update the file with valid language tag or download the latest file!`
                }
            });
            dispatch(uploadLanguageFileFailure("Invalid language tags"));
            return;
        }

        const hasDefaultLanguage = parsedData.some(item => {
            const language = item.languages?.languageTag ||
                item.descriptions?.language ||
                item.languageTag ||
                item.language;
            return language === defaultLanguage;
        });

        if (!hasDefaultLanguage) {
            dispatch(uploadLanguageFileFailure("Default language missing"));
            dispatch({
                type: SHOW_CONFIRMATION_DIALOG,
                payload: {
                    languagesToRemove: [],
                    pendingSelection: [],
                    errorMessage: `Default language ${defaultLanguage} is removed in Meta Data file, please update the file with default language or download the latest file!`
                }
            });
            return;
        }

        if (selectedLanguage && selectedLanguage.length > 0) {
            const languagesInFile = getLanguagesInFile(parsedData);
            const missingLanguages = selectedLanguage.filter(lang => !languagesInFile.has(lang));

            if (missingLanguages.length > 0) {
                const missingLanguageNames = missingLanguages.map(tag => getLocalisedName(languageData, tag)).join(', ');
                const remainingLanguages = selectedLanguage.filter(lang => languagesInFile.has(lang));

                dispatch({
                    type: SHOW_CONFIRMATION_DIALOG,
                    payload: {
                        languagesToRemove: missingLanguages,
                        pendingSelection: remainingLanguages,
                        errorMessage: `Are you sure you want to remove ${missingLanguageNames} languages?`
                    }
                });
                dispatch(uploadLanguageFileFailure("Missing selected languages"));
                return;
            }
        }

        dispatch(uploadLanguageFileStart(file));
        await runUploadProgress(dispatch);
        dispatch(uploadLanguageFileSuccess(file, parsedData));

    } catch (error) {
        if (uploadIntervalId) {
            clearInterval(uploadIntervalId);
            uploadIntervalId = null;
        }
        dispatch(removeUploadedFile());
        dispatch(uploadLanguageFileFailure(error.message));
        dispatch({
            type: SHOW_CONFIRMATION_DIALOG,
            payload: {
                languagesToRemove: [],
                pendingSelection: [],
                errorMessage: `Error: ${error.message}`
            }
        });
    }
};

export const cancelUpload = () => {
    return (dispatch) => {
        if (uploadIntervalId) {
            clearInterval(uploadIntervalId);
            uploadIntervalId = null;
        }
        dispatch(uploadLanguageFileCancel());
    };
};

export const importLanguageData = (completePayload, parsedData, onImportCallback) => {
    return async function (dispatch, getState) {
        dispatch(importLanguageDataRequest());
        try {
            console.log("importLanguageData called with:", { completePayload, parsedData });

            if (!parsedData || !Array.isArray(parsedData) || parsedData.length === 0) {
                throw new Error('Invalid data format');
            }

            const allImportedLanguages = parsedData
                .map(item => {
                    return item.languages?.languageTag ||
                        item.descriptions?.language ||
                        item.languageTag ||
                        item.language;
                })
                .filter(Boolean);

            const finalLanguages = allImportedLanguages;

            const nonDefaultLanguages = finalLanguages
                .filter(lang => lang !== completePayload?.defaultLanguage);
            dispatch(updateLanguageSelection(nonDefaultLanguages));

            const filteredParsedData = parsedData.filter(item => {
                const lang = item.languages?.languageTag ||
                    item.descriptions?.language ||
                    item.languageTag ||
                    item.language;
                return finalLanguages.includes(lang);
            });

            const transformedData = filteredParsedData.map(item => {

                const language = item.languages?.languageTag ||
                    item.descriptions?.language ||
                    item.languageTag ||
                    item.language;

                if (!language) {
                    console.error("Missing language tag for item:", item);
                    return null;
                }

                const descriptions = item.descriptions || item;

                return {
                    language: language,
                    applicationName: descriptions.applicationName || "",
                    shortDescription: descriptions.shortDescription || "",
                    longDescription: descriptions.longDescription || "",
                    customerSupportLink: descriptions.customerSupportLink || "",
                    whatIsNew:
                        descriptions.whatIsNew ||
                        descriptions.whatsNew ||
                        descriptions.newChangesAndFixes ||
                        item.newChangesAndFixes ||
                        "",
                    screenshots: Array.isArray(descriptions.screenshots) ? descriptions.screenshots : [],
                    banner: descriptions.banner || null,
                };
            }).filter(item => item !== null);

            const legalDocumentsForAllLanguages = [];
            const processedLanguages = new Set();

            filteredParsedData.forEach(item => {
                const lang = item.languages?.languageTag ||
                    item.descriptions?.language ||
                    item.languageTag ||
                    item.language;

                if (!lang || processedLanguages.has(lang)) {
                    return;
                }
                processedLanguages.add(lang);

                const legalDocumentTypeInfo = [];
                const legalDocs = item.legalDocuments?.legalDocumentTypeInfo || [];

                if (Array.isArray(legalDocs) && legalDocs.length > 0) {
                    legalDocs.forEach(doc => {
                        legalDocumentTypeInfo.push({
                            title: doc.title,
                            documentLink: doc.documentLink || ""
                        });
                    });
                } else {
                    legalDocumentTypeInfo.push(
                        { title: "Terms of Services", documentLink: item.termsOfService || "" },
                        { title: "Privacy Policy", documentLink: item.privacyPolicy || "" },
                        { title: "Open Source Software", documentLink: item.OpenSourceSoftware || "" },
                        { title: "Right to Withdrawal", documentLink: item.rightToWithdrawal || "" },
                        { title: "Imprint", documentLink: item.imprint || "" }
                    );
                }

                legalDocumentsForAllLanguages.push({
                    language: lang,
                    legalDocumentTypeInfo: legalDocumentTypeInfo
                });
            });

            const legalDocuments = legalDocumentsForAllLanguages.length > 0
                ? { legalDocumentsForAllLanguages: legalDocumentsForAllLanguages }
                : null;

            const finalPayload = {
                id: completePayload.id,
                createdAt: completePayload.createdAt,
                lastModified: completePayload.lastModified,
                name: completePayload.name,
                defaultLanguage: completePayload.defaultLanguage,
                descriptions: transformedData,
                applicationType: completePayload.applicationType,
                binary: completePayload.binary,
                binaries: completePayload.binaries,
                targeting: completePayload.targeting,
                icon: completePayload.icon,
                status: completePayload.status,
                garageModeActive: completePayload.garageModeActive,
                vehicleInstallationActive: completePayload.vehicleInstallationActive,
                hidden: completePayload.hidden,
                enableServiceIds: completePayload.enableServiceIds,
                enableProductIds: completePayload.enableProductIds,
                apkValidationMetadata: completePayload.apkValidationMetadata,
                changelog: completePayload.changelog,
                availableActions: completePayload.availableActions,
                majorVersionNumber: completePayload.majorVersionNumber,
                autoPublish: completePayload.autoPublish === undefined ? false : completePayload.autoPublish,
                ageRatingsEnabled: completePayload.ageRatingsEnabled === undefined ? false : completePayload.ageRatingsEnabled,
                dsacompliant: completePayload.dsacompliant === undefined ? false : completePayload.dsacompliant,
                isCancelable: completePayload.isCancelable,
                isDeletable: completePayload.isDeletable,
                isMajorVersion: completePayload.isMajorVersion,
                isInAppPurchase: completePayload.isInAppPurchase === undefined ? false : completePayload.isInAppPurchase,
                isPremiumApp: completePayload.isPremiumApp === undefined ? false : completePayload.isPremiumApp,
                isMonetized: completePayload.isMonetized === undefined ? false : completePayload.isMonetized,
                containAds: completePayload.containAds === undefined ? false : completePayload.containAds,
                ageAppropriateRating: completePayload.ageAppropriateRating || null,
                isAgeRatingsEnabled: completePayload.isAgeRatingsEnabled === undefined ? false : completePayload.isAgeRatingsEnabled,
                isDSACompliant: completePayload.isDSACompliant === undefined ? false : completePayload.isDSACompliant,
                paymentSellerId: completePayload.paymentSellerId || null,
                approvedAt: completePayload.approvedAt || null
            };

            console.log("Final payload prepared:", finalPayload);
            console.log("Calling onImportCallback...");

            if (onImportCallback && typeof onImportCallback === 'function') {
                await onImportCallback(finalPayload, legalDocuments, finalLanguages);
                console.log("onImportCallback completed");
            } else {
                console.error("onImportCallback is not a function:", onImportCallback);
            }

            dispatch(importLanguageDataSuccess());
        } catch (error) {
            console.error("Error in importLanguageData:", error);
            dispatch(importLanguageDataFailure(error.message));
        }
    };
};


export const downloadTemplate = (fileFormat, languageData, submissionData, selectedOptions) => {
    return async function (dispatch) {
        dispatch(downloadTemplateRequest());
        try {
            const selectedLanguage = [
                submissionData.defaultLanguage,
                ...selectedOptions.filter(lang => lang !== submissionData.defaultLanguage)
            ];

            if (fileFormat === "Excel") {
                const templateData = [];

                selectedLanguage.forEach(lang => {
                    const langData = languageData?.find(l => l.languageTag === lang);
                    const existingDesc = submissionData?.descriptions?.find(d => d.language === lang);
                    const legalLinks = submissionData?.legalDocuments?.legalLinkInfos?.[lang]?.legalLinks || {};

                    if (langData) {
                        templateData.push({
                            languageTag: langData.languageTag || "",
                            defaultName: langData.defaultName || "",
                            defaultCountry: langData.defaultCountry || "",
                            localisedCountry: langData.localisedCountry || "",
                            localisedName: langData.localisedName || "",
                            language: langData.languageTag || "",
                            screenshots: existingDesc?.screenshots || [],
                            applicationName: existingDesc?.applicationName || "",
                            shortDescription: existingDesc?.shortDescription || "",
                            longDescription: existingDesc?.longDescription || "",
                            customerSupportLink: existingDesc?.customerSupportLink || "",
                            newChangesAndFixes: existingDesc?.whatIsNew || "",
                            termsOfService: legalLinks["Terms of Services"] || "",
                            privacyPolicy: legalLinks["Privacy Policy"] || "",
                            OpenSourceSoftware: legalLinks["Open Source Software"] || "",
                            rightToWithdrawal: legalLinks["Right to Withdrawal"] || "",
                            imprint: legalLinks["Imprint"] || "",
                        });
                    }
                });

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(templateData);
                ws['!cols'] = new Array(18).fill({ wch: 20 });
                XLSX.utils.book_append_sheet(wb, ws, "Languages");
                XLSX.writeFile(wb, "language_template.xlsx");

            } else if (fileFormat === "JSON") {
                const jsonData = selectedLanguage.map(lang => {
                    const langData = languageData.find(l => l.languageTag === lang);
                    const existingDesc = submissionData.descriptions?.find(d => d.language === lang);
                    const legalLinks = submissionData?.legalDocuments?.legalLinkInfos?.[lang]?.legalLinks || {};

                    const legalDocumentTypeInfo = [
                        { title: "Terms of Services", documentLink: legalLinks["Terms of Services"] || "" },
                        { title: "Privacy Policy", documentLink: legalLinks["Privacy Policy"] || "" },
                        { title: "Open Source Software", documentLink: legalLinks["Open Source Software"] || "" },
                        { title: "Right to Withdrawal", documentLink: legalLinks["Right to Withdrawal"] || "" },
                        { title: "Imprint", documentLink: legalLinks["Imprint"] || "" }
                    ];

                    return {
                        languages: {
                            languageTag: langData?.languageTag || lang,
                            defaultName: langData?.defaultName || "",
                            defaultCountry: langData?.defaultCountry || "",
                            localisedCountry: langData?.localisedCountry || "",
                            localisedName: langData?.localisedName || ""
                        },
                        descriptions: {
                            language: langData?.languageTag || lang,
                            screenshots: existingDesc?.screenshots || [],
                            applicationName: existingDesc?.applicationName || "",
                            shortDescription: existingDesc?.shortDescription || "",
                            longDescription: existingDesc?.longDescription || "",
                            customerSupportLink: existingDesc?.customerSupportLink || "",
                            newChangesAndFixes: existingDesc?.whatIsNew || null
                        },
                        legalDocuments: {
                            language: langData?.languageTag || lang,
                            legalDocumentTypeInfo: legalDocumentTypeInfo
                        }
                    };
                });

                const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'language_template.json';
                a.click();
                URL.revokeObjectURL(url);
            }

            dispatch(downloadTemplateSuccess());
        } catch (error) {
            dispatch(downloadTemplateFailure(error.message));
        }
    };
};